import { useEffect, useState } from 'react';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function AppStatus() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    const onlineHandler = () => setOnline(true);
    const offlineHandler = () => setOnline(false);
    const installHandler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const controllerHandler = () => window.location.reload();
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('beforeinstallprompt', installHandler);
    navigator.serviceWorker?.addEventListener('controllerchange', controllerHandler);
    void navigator.serviceWorker?.getRegistration().then((registration) => {
      if (!registration) return;
      if (registration.waiting) setWaiting(registration.waiting);
      registration.addEventListener('updatefound', () => {
        registration.installing?.addEventListener('statechange', () => {
          if (registration.waiting) setWaiting(registration.waiting);
        });
      });
    });
    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
      window.removeEventListener('beforeinstallprompt', installHandler);
      navigator.serviceWorker?.removeEventListener('controllerchange', controllerHandler);
    };
  }, []);

  if (online && !installPrompt && !waiting) return null;

  return (
    <aside aria-live="polite" className={`app-status${online ? '' : ' is-offline'}`}>
      <span>
        <strong>{online ? 'Her Closet update' : 'Offline browsing'}</strong>
        <small>
          {online
            ? installPrompt
              ? 'Install Her Closet on this device.'
              : 'A fresh version is ready.'
            : 'Your saved closet snapshot is available. Browsing is read-only until you reconnect.'}
        </small>
      </span>
      {installPrompt ? (
        <button
          type="button"
          onClick={() => {
            void installPrompt.prompt().then(() => setInstallPrompt(null));
          }}
        >
          Install
        </button>
      ) : null}
      {waiting ? (
        <button type="button" onClick={() => waiting.postMessage({ type: 'SKIP_WAITING' })}>
          Update
        </button>
      ) : null}
    </aside>
  );
}
